import appleIcon from '@/assets/logos/apple.svg';
import { KINDE_APPLE_CONNECTION_ID } from '@/components/constants/kinde';
import { iframeSafeAuthRoute } from '@/components/modal/sign-in-modal/utils';
import { IS_AUTH0_SOCIAL_LOGIN_APPLE_ENABLED } from '@/features/flags/legacy-flags';
import { signInClicked } from '@/lib/analytics/scribe/auth';
import { useDesktopAuthSafe } from '@/tauri/auth0/desktop-auth-context';
import { beginDesktopKindeAttempt } from '@/tauri/auth0/login';
import { useDesktopLoginPending } from '@/tauri/auth0/use-desktop-login-pending';
import { isTauri, isUsingReactKinde } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { useTranslations } from '@/utils/translations';
import { AUTH0_APPLE_CONNECTION, AUTH0_LOGIN_PATH } from '@heidi/auth/contracts';
import { Button } from '@heidi/design-system/button';
import { Loader } from '@heidi/snowflake-ui/loader';
import { toAuth0UiLocales } from '@heidi/utils/i18n';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import Image from 'next/image';
import { useState } from 'react';

interface AppleLoginButtonProps {
  /** Callback when button is clicked (before auth redirect) */
  onClick?: () => void;
  /** Callback when desktop auth login fails before redirect completes */
  onLoginFailed?: () => void;
  /** External disabled state (e.g., when another button is loading) */
  disabled?: boolean;
  /** Full redirect URI including locale prefix */
  postLoginRedirectUri: string;
  /** Button size. @default 'lg' */
  size?: 'sm' | 'md' | 'lg' | 'touch';
  /**
   * Overrides the locale used for Auth0 `ui_locales` / Kinde `lang`. Pre-auth
   * desktop flows must pass the react-intl locale here — `useTranslations()`
   * otherwise falls back to `router.locale` (always `en` on desktop).
   */
  localeOverride?: string;
}

/**
 * Apple social login button that conditionally renders Kinde or Auth0 based on feature flag.
 */
export function AppleLoginButton({
  onClick,
  onLoginFailed,
  disabled,
  postLoginRedirectUri,
  size = 'lg',
  localeOverride,
}: AppleLoginButtonProps) {
  const { t, locale } = useTranslations({ localeOverride });
  const { login: kindeLogin } = useKindeAuth();
  const { login: desktopAuthLogin } = useDesktopAuthSafe();
  const shouldUseDesktopAuth = isTauri();
  const [isLoading, setIsLoading] = useState(false);

  // Desktop auth resolves in-place (no redirect): hold the loading state from
  // the click until the attempt settles (session, auth error, launch failure,
  // or the 5s abandonment watchdog), so a second click can never start a
  // second PKCE attempt while the browser leg runs (MUL-3346). The shared
  // pending flag also disables sibling sign-in buttons on every surface.
  const isDesktopLoginPending = useDesktopLoginPending(() => setIsLoading(false));
  const isDisabled = disabled || isLoading || isDesktopLoginPending;

  // Kinde branches: exact pre-MUL-3346 semantics (spinner on click; local
  // loading disables on web only — on web a Kinde click really does navigate
  // the page away, so there is nothing left to unlock; on desktop the click is
  // marked pending via beginDesktopKindeAttempt so the shared flag governs it) plus exclusion while a
  // genuine desktop attempt (shared pending) is in flight. This deliberately
  // differs from DBLoginButton's Kinde guard (isLoading disables everywhere):
  // each button keeps ITS OWN pre-PR expression, and Kinde clicks never mint
  // a PKCE attempt, so the MUL-3346 single-slot hazard does not apply here.
  const isKindeDisabled =
    disabled || (!shouldUseDesktopAuth && isLoading) || isDesktopLoginPending;

  const buildAuth0AppleLoginUrl = () => {
    const params = new URLSearchParams();
    params.set('returnTo', postLoginRedirectUri);
    params.set('connection', AUTH0_APPLE_CONNECTION);
    if (locale) {
      params.set('ui_locales', toAuth0UiLocales(locale));
    }
    return `${AUTH0_LOGIN_PATH}?${params.toString()}`;
  };

  const handleClick = () => {
    signInClicked('apple');
    setIsLoading(true);
    onClick?.();
  };

  const appleIconNode = (
    <Image
      src={appleIcon}
      loading="lazy"
      alt="apple logo"
      className="dark-invert size-4.5"
    />
  );

  // Auth0 path
  if (IS_AUTH0_SOCIAL_LOGIN_APPLE_ENABLED()) {
    return (
      <Button
        disableAutomaticSvgSizing
        className="w-full"
        disabled={isDisabled}
        variant="secondary"
        size={size}
        type="button"
        onClick={() => {
          handleClick();
          if (shouldUseDesktopAuth) {
            void desktopAuthLogin({
              connection: AUTH0_APPLE_CONNECTION,
              uiLocales: toAuth0UiLocales(locale),
            })
              .then((launched) => {
                // Positive signal from login(): `false` = the call was a
                // no-op (outside DesktopAuthProvider / bridge unavailable),
                // so neither .catch nor a pending falling edge will ever
                // release this button or the surface's shared in-progress
                // state. NOT inferred from pending-state absence — that is
                // also the post-settle state, and a fast settle must not
                // surface a false failure (MUL-3346 review).
                if (!launched) {
                  setIsLoading(false);
                  onLoginFailed?.();
                }
              })
              .catch((error) => {
                logger.error('[Auth0] Desktop Apple login failed', error);
                // The launch never marked an attempt pending (or already
                // cleared it), so release the spinner here.
                setIsLoading(false);
                onLoginFailed?.();
              });
            return;
          }
          const loginUrl = buildAuth0AppleLoginUrl();
          iframeSafeAuthRoute(loginUrl);
        }}
      >
        {isLoading ? <Loader className="size-4.5" /> : appleIconNode}
        {t('auth.continueWithApple')}
      </Button>
    );
  }

  // Kinde path - React Kinde (Button)
  if (isUsingReactKinde()) {
    return (
      <Button
        disableAutomaticSvgSizing
        className="w-full"
        disabled={isKindeDisabled}
        variant="secondary"
        size={size}
        type="button"
        onClick={() => {
          handleClick();
          // Desktop: this hands off to the system browser and never comes
          // back with a settle signal — mark the attempt so the abandonment
          // watchdog releases the spinner and the surface's shared disable.
          beginDesktopKindeAttempt();
          void kindeLogin({
            authUrlParams: {
              post_login_redirect_url: postLoginRedirectUri,
              connection_id: KINDE_APPLE_CONNECTION_ID,
              lang: locale,
            },
            app_state: {
              redirectTo: postLoginRedirectUri,
            },
          });
        }}
      >
        {isLoading ? <Loader className="size-4.5" /> : appleIconNode}
        {t('auth.continueWithApple')}
      </Button>
    );
  }

  // Kinde apple
  return (
    <Button
      disableAutomaticSvgSizing
      className="w-full"
      disabled={isKindeDisabled}
      variant="secondary"
      size={size}
      type="button"
      onClick={() => {
        handleClick();
        const url = new URL('/api/auth/login', window.location.origin);
        url.searchParams.set('post_login_redirect_url', postLoginRedirectUri);
        url.searchParams.set('connection_id', KINDE_APPLE_CONNECTION_ID);
        iframeSafeAuthRoute(url.toString());
      }}
    >
      {isLoading ? <Loader className="size-4.5" /> : appleIconNode}
      {t('auth.continueWithApple')}
    </Button>
  );
}
